/**
 * Handler de imagen optimizada para editores Quill (PrimeNG `p-editor`).
 *
 * Reemplaza el handler de imagen por defecto de Quill —que incrusta el archivo
 * crudo como base64— por uno que primero optimiza la imagen (redimensiona +
 * recomprime) en el navegador. Así las imágenes viajan embebidas en el HTML del
 * mensaje pero con un peso razonable (opción D). Ver `image-optimizer.ts`.
 *
 * ⚠️ Recordatorio: si la carga de imágenes embebidas vuelve a dar problemas,
 * migrar a la "opción A" (URL firmada servida por el backend).
 */

import { ImageOptimizeOptions, estimateDataUrlBytes, optimizeImageToDataUrl } from './image-optimizer';

/** Subconjunto de la API de Quill que necesitamos (evita depender del tipo completo). */
export interface QuillLike {
  getModule(name: string): { addHandler(name: string, handler: () => void): void } | null;
  getSelection(focus?: boolean): { index: number; length: number } | null;
  insertEmbed(index: number, type: string, value: unknown, source?: string): void;
  setSelection(index: number, length: number, source?: string): void;
}

type OptimizeFn = (file: File, options?: ImageOptimizeOptions) => Promise<string>;

export interface OptimizedImageHandlerOptions {
  /** Opciones de optimización (lado máximo, calidad, formato). */
  optimize?: ImageOptimizeOptions;
  /** Tope de bytes tras optimizar; por encima se descarta y se avisa. Por defecto 300 KB. */
  maxBytes?: number;
  /** Se invoca si la imagen optimizada supera `maxBytes`. */
  onTooLarge?: (bytes: number) => void;
  /** Se invoca ante cualquier error de optimización/lectura. */
  onError?: (err: unknown) => void;
}

const DEFAULT_MAX_BYTES = 300 * 1024;

/**
 * Optimiza un archivo y lo inserta como imagen en la posición actual del cursor.
 * Aislado del DOM (recibe el `File` ya elegido) para poder testearlo; la función
 * de optimización es inyectable para los tests.
 */
export async function insertOptimizedImage(
  quill: QuillLike,
  file: File,
  options: OptimizedImageHandlerOptions & { optimize?: ImageOptimizeOptions } = {},
  optimizeFn: OptimizeFn = optimizeImageToDataUrl
): Promise<void> {
  const maxBytes = options.maxBytes ?? DEFAULT_MAX_BYTES;
  try {
    const dataUrl = await optimizeFn(file, options.optimize);
    const bytes = estimateDataUrlBytes(dataUrl);
    if (bytes > maxBytes) {
      options.onTooLarge?.(bytes);
      return;
    }
    const range = quill.getSelection(true);
    const index = range ? range.index : 0;
    quill.insertEmbed(index, 'image', dataUrl, 'user');
    quill.setSelection(index + 1, 0, 'silent');
  } catch (err) {
    options.onError?.(err);
  }
}

/**
 * Registra el handler optimizado en la toolbar del Quill dado. Al pulsar el
 * botón de imagen abre un selector de archivo, optimiza y lo inserta.
 */
export function registerOptimizedImageHandler(quill: QuillLike, options: OptimizedImageHandlerOptions = {}): void {
  const toolbar = quill.getModule('toolbar');
  if (!toolbar) return;

  toolbar.addHandler('image', () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = () => {
      const file = input.files?.[0];
      if (file) void insertOptimizedImage(quill, file, options);
    };
    input.click();
  });
}
